"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { flushSync } from "react-dom";
import { useLeadFormConfig } from "@/utils/hooks/useLeadFormConfig";
import Image from "next/image";
import { submitLead } from "@/app/utilities/apis";
import {
    storeLeadSubmission,
    hasSubmittedLead,
    hasSplashPopupBeenConsumedForSession,
    markSplashPopupConsumedForSession,
} from "@/utils/leadTracking";
import ThanksPopup from '@/component/ThanksPopup';
import Notification from '../../Notification';
import AlreadySubmitted from '@/component/ui/AlreadySubmitted';
// LeadForm handles location and cookie defaults
import { acquireModalSession, releaseModalSession, subscribeModalSession, getActiveModalSession } from "@/lib/client/modalSession";
import LeadForm from '@/component/ui/Lead/LeadForm';
import LeadOtpStep from '@/component/ui/Lead/LeadOtpStep';
import LeadDealerStep from '@/component/ui/Lead/LeadDealerStep';
import { PRODUCT_DETAIL_TRACKING } from "@/lib/constants/tracking";
import { SPLASH_POPUP_SESSION_ID, THANKS_POPUP_SESSION_ID } from "@/app/utilities/constants";

const FORM_TYPE_GORP = "GORP";
/** Auto-open delay after page load (and after another modal closes). */
const SPLASH_POPUP_AUTO_OPEN_DELAY_MS = 15_000;

interface Props {
    model: any;
    heading: string | React.ReactNode;
    LeadPopupSubmitTracking?: string;
}

// LeadForm will read user cookie and location; helpers removed here

const Splashpopup: React.FC<Props> = ({ model, heading, LeadPopupSubmitTracking }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showThanks, setShowThanks] = useState(false);
    const [lastLeadData, setLastLeadData] = useState<any>(null);
    const [notification, setNotification] = useState({ message: '', isError: false, visible: false });
    const [otpState, setOtpState] = useState<{ leadId: string; phone: string; payload: any } | null>(null);
    // Set once a lead is confirmed on a dealer-selection model; shows the dealer step
    const [dealerState, setDealerState] = useState<{ leadId: string; payload: any } | null>(null);
    const { emailRequired, dealerSelectionEnabled, loading: leadConfigLoading } = useLeadFormConfig(model?._id);

    const pendingTimerRef = useRef<number | null>(null);
    const pendingUnsubRef = useRef<(() => void) | null>(null);

    const showNotification = useCallback((message: string, isError = false) => {
        setNotification({ message, isError, visible: true });
        setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 3000);
    }, []);

    // Opens splash popup, but if another modal is active, waits for it to close and then opens after SPLASH_POPUP_AUTO_OPEN_DELAY_MS
    const openSplashPopup = useCallback(() => {
        if (typeof window !== "undefined" && hasSplashPopupBeenConsumedForSession()) {
            return;
        }

        const active = getActiveModalSession();

        if (active && active !== SPLASH_POPUP_SESSION_ID) {
            // Another modal is active — wait for it to close, then show after the standard delay
            if (pendingUnsubRef.current) {
                pendingUnsubRef.current();
                pendingUnsubRef.current = null;
            }

            const unsub = subscribeModalSession((activeId) => {
                if (!activeId) {
                    if (pendingTimerRef.current) {
                        clearTimeout(pendingTimerRef.current as number);
                    }
                    pendingTimerRef.current = window.setTimeout(() => {
                        if (hasSplashPopupBeenConsumedForSession()) {
                            pendingTimerRef.current = null;
                            return;
                        }
                        markSplashPopupConsumedForSession();
                        acquireModalSession(SPLASH_POPUP_SESSION_ID);
                        setIsVisible(true);
                        pendingTimerRef.current = null;
                    }, SPLASH_POPUP_AUTO_OPEN_DELAY_MS);

                    if (unsub) unsub();
                }
            });

            pendingUnsubRef.current = unsub;
            return;
        }

        markSplashPopupConsumedForSession();
        acquireModalSession(SPLASH_POPUP_SESSION_ID);
        setIsVisible(true);
    }, []);

    const openSplashPopupFromUserGesture = useCallback(() => {
        if (typeof window !== "undefined" && hasSplashPopupBeenConsumedForSession()) {
            return;
        }

        const active = getActiveModalSession();
        if (active && active !== SPLASH_POPUP_SESSION_ID) {
            openSplashPopup();
            return;
        }

        flushSync(() => {
            markSplashPopupConsumedForSession();
            acquireModalSession(SPLASH_POPUP_SESSION_ID);
            setIsVisible(true);
        });
    }, [openSplashPopup]);

    // Closes splash popup and clears any pending timers/subscriptions related to opening it
    const closeSplashPopup = useCallback(() => {
        markSplashPopupConsumedForSession();
        setIsVisible(false);
        releaseModalSession(SPLASH_POPUP_SESSION_ID);

        // clear any pending open timers/subscriptions
        if (pendingTimerRef.current) {
            clearTimeout(pendingTimerRef.current as number);
            pendingTimerRef.current = null;
        }
        if (pendingUnsubRef.current) {
            pendingUnsubRef.current();
            pendingUnsubRef.current = null;
        }
    }, []);

    // Shortcut handler for the "already submitted" dialog
    const closeAlreadySubmitted = useCallback(() => {
        closeSplashPopup();
    }, [closeSplashPopup]);

    const openThanksPopup = useCallback(() => {
        acquireModalSession(THANKS_POPUP_SESSION_ID);
        setShowThanks(true);
    }, []);

    const closeThanksPopup = useCallback(() => {
        setShowThanks(false);
        releaseModalSession(THANKS_POPUP_SESSION_ID);
    }, []);

    // Automatically show splash popup after page load, but only if user hasn't already submitted a lead for this model
    useEffect(() => {
        if (
            !model?._id
            || typeof window === "undefined"
            || hasSplashPopupBeenConsumedForSession()
            || hasSubmittedLead(model._id, FORM_TYPE_GORP, model?.variant_id)
        ) {
            return;
        }

        const timer = setTimeout(() => openSplashPopup(), SPLASH_POPUP_AUTO_OPEN_DELAY_MS);
        return () => clearTimeout(timer);
    }, [model?._id, openSplashPopup]);

    // Listen for global events to open splash popup (used by LeadButton when useSplashPopup is true)
    useEffect(() => {
        const openHandler = (event: Event) => {
            const customEvent = event as CustomEvent<{ carId?: string, variantId?: string }>;
            if (customEvent?.detail?.carId && model?._id && customEvent.detail.carId !== model._id) return;
            openSplashPopupFromUserGesture();
        };

        window.addEventListener('open-splash-popup', openHandler as EventListener);
        return () => window.removeEventListener('open-splash-popup', openHandler as EventListener);
    }, [model?._id, openSplashPopupFromUserGesture]);

    useEffect(() => {
        const unsubscribe = subscribeModalSession((activeId) => {
            if (activeId !== SPLASH_POPUP_SESSION_ID) {
                setIsVisible(false);
            }
            if (activeId !== THANKS_POPUP_SESSION_ID) {
                setShowThanks(false);
            }
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        return () => {
            releaseModalSession(SPLASH_POPUP_SESSION_ID);
            releaseModalSession(THANKS_POPUP_SESSION_ID);
            if (pendingTimerRef.current) {
                clearTimeout(pendingTimerRef.current as number);
                pendingTimerRef.current = null;
            }
            if (pendingUnsubRef.current) {
                pendingUnsubRef.current();
                pendingUnsubRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        document.body.style.overflow = isVisible ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [isVisible]);

    // Lead defaults are handled inside the shared LeadForm component
    const handleClose = closeSplashPopup;
    const alreadySubmitted = !dealerState && typeof window !== 'undefined' && model?._id && hasSubmittedLead(model._id, FORM_TYPE_GORP, model?.variant_id);

    // Shared success path for both the no-OTP flow and the OTP-verified flow —
    // marking the lead "submitted" (and opening the thanks popup) only happens
    // once a lead is actually complete, not merely first submitted.
    const finalizeLeadSuccess = useCallback((payload: any) => {
        const carName = `${model?.brand?.name} ${model?.name}`;

        if (typeof window !== "undefined" && model?._id) {
            storeLeadSubmission(model._id, carName, FORM_TYPE_GORP, model?.variant_id);
            (window as any).__LAST_LEAD_DATA__ = payload;
        }

        window.dispatchEvent(
            new CustomEvent("lead-submitted", {
                detail: { carId: model._id, carName },
            }),
        );

        setLastLeadData(payload);
        setOtpState(null);
        setDealerState(null);
        openThanksPopup();
        handleClose();
    }, [model, openThanksPopup, handleClose]);

    // Runs once a lead is confirmed — routes through the dealer step first for
    // dealer-selection models.
    const handleLeadConfirmed = (leadId: string | undefined, payload: any) => {
        if (dealerSelectionEnabled && leadId) {
            setOtpState(null);
            setDealerState({ leadId, payload });
            return;
        }
        finalizeLeadSuccess(payload);
    };

    // The lead is already complete by the dealer step, so dismissing the popup
    // there still finishes it as a success.
    const handleDismiss = dealerState ? () => finalizeLeadSuccess(dealerState.payload) : handleClose;

    return (
        <>
            {isVisible && alreadySubmitted ? (
                <AlreadySubmitted onClose={closeAlreadySubmitted} />
            ) : isVisible ? (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-100" onClick={handleDismiss}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-[24px] w-[90%] sm:w-[450px] relative transition-transform duration-300"
                    >
                        <button
                            onClick={handleDismiss}
                            className="absolute top-3 right-3 z-20 w-[30px] h-[30px] p-[4px] rounded-full flex items-center justify-center cursor-pointer"
                        >
                            <Image src="https://static-asset.tractorjunction.com/cj/popup-close.svg" alt="close" width={40} height={40} className="w-10 h-10" />
                        </button>

                        <div className="flex justify-between items-center pt-4 pr-12">
                            <div className="pl-4 pt-4 pb-0">
                                <p className="text-[20px] leading-[36px] font-medium tracking-[-0.75px] font-roboto text-gray-800 relative line-clamp-2">
                                    {heading || "Get On Road Price"}
                                </p>
                            </div>
                        </div>

                        <div className="p-4 md:p-[18px] mb-3">
                            {dealerState ? (
                                <LeadDealerStep
                                    leadId={dealerState.leadId}
                                    carName={`${model?.brand_name || model?.brand?.name} ${model?.name}`}
                                    onDone={() => finalizeLeadSuccess(dealerState.payload)}
                                />
                            ) : otpState ? (
                                <LeadOtpStep
                                    leadId={otpState.leadId}
                                    phone={otpState.phone}
                                    onVerified={() => handleLeadConfirmed(otpState.leadId, otpState.payload)}
                                    onCancel={() => setOtpState(null)}
                                />
                            ) : (
                            <LeadForm
                                disableMobileAutofocus
                                submitLabel={isSubmitting ? "Submitting..." : "Submit"}
                                LeadPopupSubmitTracking={PRODUCT_DETAIL_TRACKING.LEAD_POPUP_SUMBIT}
                                onSubmit={async (form) => {
                                    if (model?._id && hasSubmittedLead(model._id, FORM_TYPE_GORP, model?.variant_id)) {
                                        const carName = `${model?.brand_name || model?.brand?.name} ${model?.name}`;
                                        try { storeLeadSubmission(model._id, carName, FORM_TYPE_GORP, model?.variant_id); } catch { }
                                        showNotification("You have already submitted the enquiry for this product, our team will reach out to you shortly.");
                                        return false;
                                    }

                                    setIsSubmitting(true);
                                    try {
                                        const baseVariant = Array.isArray(model?.base_variant) ? model?.base_variant[0] : model?.base_variant;
                                        const selectedPrice = model?.price_range?.base ?? baseVariant?.price ?? model?.price ?? null;

                                        const { buildLeadPayload } = await import('@/app/utilities/locationUtils');
                                        const base = buildLeadPayload(form, { carId: model?._id, variantId: model?.variant_id });
                                        const payload = {
                                            ...base,
                                            variantName: model?.variant_name,
                                            brand: model?.brand_name || model?.brand?.name,
                                            modelName: model?.name,
                                            onRoadPrice: selectedPrice,
                                            exShowroomPrice: selectedPrice,
                                        };

                                        const response = await submitLead(payload);

                                        if (response?.status === "otp_required") {
                                            setOtpState({ leadId: response.data.lead_id, phone: response.data.phone, payload });
                                            return;
                                        } else if (response?.status === "success") {
                                            handleLeadConfirmed(response.data?._id, payload);
                                            return true;
                                        } else {
                                            showNotification("Failed to submit. Please try again.", true);
                                            return false;
                                        }
                                    } catch (err) {
                                        console.error("Lead submission failed:", err);
                                        showNotification("Something went wrong. Please try again later.", true);
                                        return false;
                                    } finally {
                                        setIsSubmitting(false);
                                    }
                                }}
                                onError={(msg) => showNotification(msg, true)}
                                showEmail={emailRequired}
                                disabled={leadConfigLoading}
                            />
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            <Notification message={notification.message} isVisible={notification.visible} isError={notification.isError} />
            <ThanksPopup
                isOpen={showThanks}
                onClose={closeThanksPopup}
                modelId={model?._id}
                leadData={lastLeadData}
                model={model}
            />
        </>
    );
};

export default Splashpopup;